import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import dashLogo from '../../images/HUGHES-DIGITAL-SIGNAGE_BLUE.png';

interface EditPasswordProps {
  actionUrl: string;
  resetPasswordToken: string;
}

export function MssEditPassword({ actionUrl, resetPasswordToken }: EditPasswordProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
  const inputType = showPassword ? 'text' : 'password';

  return (
    <div className='min-h-screen bg-zinc-50 flex items-center justify-center p-8'>
      <div className='w-full max-w-md'>
        <div className='flex items-center justify-center mb-8'>
          <img src={dashLogo} alt='Hughes Digital Signage' className='w-full' />
        </div>

        <h1 className='text-xl font-semibold text-zinc-900 mb-2'>Change your password</h1>
        <p className='text-sm text-zinc-500 mb-6'>Enter and confirm your new password below.</p>

        <form
          action={actionUrl}
          method='post'
          onSubmit={() => setIsLoading(true)}
          className='space-y-5'
        >
          <input type='hidden' name='authenticity_token' value={csrfToken} />
          <input type='hidden' name='_method' value='put' />
          <input type='hidden' name='user[reset_password_token]' value={resetPasswordToken} />

          <div className='space-y-1'>
            <Label htmlFor='password' className='text-sm font-normal text-black-950'>New password</Label>
            <div className='relative'>
              <Input
                id='password'
                name='user[password]'
                type={inputType}
                placeholder='New password'
                autoFocus
                required
                className='h-11 pr-10 bg-[#ededef] shadow-none focus-visible:ring-3 focus-visible:ring-zinc-300 focus-visible:border focus-visible:border-ring'
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors'
              >
                {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            </div>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='password_confirmation' className='text-sm font-normal text-black-950'>Confirm password</Label>
            <Input
              id='password_confirmation'
              name='user[password_confirmation]'
              type={inputType}
              placeholder='Confirm password'
              required
              className='h-11 bg-[#ededef] shadow-none focus-visible:ring-3 focus-visible:ring-zinc-300 focus-visible:border focus-visible:border-ring'
            />
          </div>

          <Button
            type='submit'
            className='w-full h-11 bg-blue-700 hover:bg-blue-800 transition-colors'
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2' />
                Saving...
              </>
            ) : (
              'Change my password'
            )}
          </Button>
        </form>

        <div className='mt-6 text-center'>
          <p className='text-sm text-zinc-600'>
            Remember your password?{' '}
            <a href='/users/sign_in' className='text-blue-600 hover:text-blue-700 transition-colors'>
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default MssEditPassword;
